/**
 * PLATFORM CONSOLE — the other surface. 09 §4.16.
 *
 * PLATFORM and TENANT never bleed and are never simultaneous (04 I6). Entering here is an explicit
 * action from the context bar, not a link that happens to work, and crossing wipes every
 * tenant-scoped slot. The chrome changes with `data-surface="PLATFORM"` so the two are
 * unmistakable at a glance — and §9.5 A3 means the surface is written out in words as well.
 *
 * 09 §3.3: on a platform screen the COMPANY slot is absent, not greyed and not `—`.
 *
 * The tiles below are the platform parents from §4.16. They lead to screens that do not exist yet;
 * each says so rather than pretending.
 */

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { StateBlock } from '../components/StateBlock';
import { routes } from '../routes';

/**
 * A tile with `href` leads somewhere; a tile without one says so. The distinction is the point:
 * G-03's pattern is a surface that is "written, correct, documented as working, and reaching
 * nothing", so a parent that has no screen behind it says exactly that rather than opening a stub.
 */
interface PlatformParent {
  readonly titleHe: string;
  readonly leadHe: string;
  readonly href?: string;
}

const PLATFORM_PARENTS: readonly PlatformParent[] = [
  {
    titleHe: 'לקוחות',
    leadHe: 'המשרדים שהפלטפורמה מנהלת, מצבם, מספר החברות והמשתמשים בכל אחד, והקמת לקוח חדש.',
    href: routes.platformTenants,
  },
  {
    // The bridge into a customer's data, and the queue an approver stands in front of. A place of
    // its own because a grant is requested by one principal and approved by a different one.
    titleHe: 'הרשאות תמיכה',
    leadHe: 'בקשות כניסה לנתוני משרד, מה חוסם כל אחת, אישור וביטול.',
    href: routes.platformSupportGrants,
  },
  {
    titleHe: 'מצב הנתונים',
    leadHe: 'מתי רצה השרשרת הלילית, מה עבר ומה נכשל, וטריות הנתונים לפי חברה.',
  },
  {
    titleHe: 'האבטחה שלי',
    leadHe: 'איפה החשבון שלך מחובר, ואימות דו-שלבי. חובה לחשבון פלטפורמה ואינה ניתנת לביטול.',
    href: routes.personalSecurity,
  },
];

export function PlatformConsoleScreen(): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'PLATFORM') {
    // Reaching this address without the PLATFORM surface is not a bug to paper over: the surface is
    // a property of the SESSION, so the app states the situation instead of switching silently.
    return (
      <AppShell titleHe="קונסולת הפלטפורמה">
        <StateBlock
          titleHe="אתה נמצא בסביבת המשרד"
          bodyHe="קונסולת הפלטפורמה היא סביבה נפרדת, והמעבר אליה הוא פעולה מפורשת."
          tone="unavailable"
          nextStep={<p>אפשר לעבור אליה מהפינה השמאלית של סרגל ההקשר.</p>}
        />
      </AppShell>
    );
  }

  return (
    <AppShell titleHe="קונסולת הפלטפורמה">
      <section className="tiles">
        {PLATFORM_PARENTS.map((p) => (
          <article
            key={p.titleHe}
            className="tile"
            data-available={p.href === undefined ? 'false' : 'true'}
          >
            <h2 className="tile__title">{p.titleHe}</h2>
            <p className="tile__lead">{p.leadHe}</p>
            {p.href === undefined ? (
              <p className="tile__unavailable">לא זמין — החלק הזה טרם נפתח במערכת</p>
            ) : (
              <a className="btn btn--primary" href={p.href}>
                פתיחה
              </a>
            )}
          </article>
        ))}
      </section>
    </AppShell>
  );
}
